import React, { useState } from "react";
import { Check } from "lucide-react";
import { BFS_MAPPING } from "./WizardConstants";
import {
  DEPOSIT_METHOD_OPTIONS,
  WITHDRAWAL_METHOD_OPTIONS,
  PROCEEDS_OPTIONS,
  SECURITY_PURCHASES_OPTIONS,
  DIVIDEND_OPTIONS,
  DEFAULT_DIVIDEND_OPTION,
  TYPE_OF_TRANSFER_OPTIONS,
  FREQUENCY_OPTIONS,
  normalizeAccountOptionValue,
  LIQUIDITY_NEEDS_OPTIONS,
  LIQUIDITY_TIME_HORIZON_OPTIONS,
  getLiquidityNeedsLabel,
  getLiquidityTimeHorizonLabel,
} from "../../constants/accountConstants";
import {
  PRIMARY_OBJECTIVE_OPTIONS,
  RISK_TOLERANCE_OPTIONS,
  TIME_HORIZON_OPTIONS,
  DECISION_MAKING_OPTIONS,
  SOURCE_OF_FUNDS_OPTIONS,
  PRIMARY_USE_OPTIONS,
  getAccountRiskTimeHorizonLabel,
} from "../../constants/householdConstants";
import { formatNumericInput } from "../../utils/validationHelpers";
import { getAccountOwnerFirstNamesLabel, formatDateForDisplay } from "../../utils/formatters";
import {
  getDestinationIdentifierLabel,
  getDestinationIdentifierOptions,
} from "../../utils/destinationIdentifiers";
import { getDatePickerBoundsForConstraint } from "../../utils/dateFieldConstraints";
import DateInput from "../DateInput";
import {
  computeBrokerageFlags,
  showAccountDecisionMaking,
  showSourceOfFunds,
  showUseOfFunds,
  showLiquidityRequirements,
  showAccountRiskDetails,
} from "../../utils/brokerageFields";

// Buffered Input Component to prevent API thrashing
const BufferedInput = ({ value, onCommit, type = "text", ...props }) => {
    const [localVal, setLocalVal] = useState(value);
    
    // Sync local state if parent value changes
    React.useEffect(() => {
        setLocalVal(value);
    }, [value]);

    return (
        <input 
            type={type}
            {...props}
            value={localVal}
            onChange={(e) => {
                let val = e.target.value;
                if (type === "number") {
                    val = formatNumericInput(val, true);
                }
                setLocalVal(val);
            }}
            onBlur={() => {
                if (localVal !== value) onCommit(localVal);
            }}
            onKeyDown={(e) => {
                if (type === "date") e.preventDefault();
                if (e.key === 'Enter') e.target.blur();
            }}
            onPaste={(e) => {
                if (type === "date") e.preventDefault();
            }}
            onWheel={(e) => {
                if (type === "date") e.target.blur();
            }}
        />
    );
};

// Sub-component for a Transaction Item (Deposit or Withdrawal)
const TransactionItem = ({ type, data, onChange, onRemove, defaults, index, household, accountId }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const scheduleBounds = getDatePickerBoundsForConstraint("schedule_future");

    const summary = data.amount 
        ? `$${data.amount} - ${data.frequency}` 
        : `New ${type.slice(0, -1)}`; // "New Deposit" or "New Withdrawal"

    const isDeposit = type === 'Deposits';

    return (
        <div className="bg-white border border-gray-200 rounded-xl mb-3 shadow-sm hover:shadow-md transition-all overflow-hidden">
            {/* Header / Summary */}
            <div 
                className="flex items-center justify-between p-4 cursor-pointer bg-gray-50/50 hover:bg-gray-100 transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isDeposit ? 'bg-purple-100 text-purple-600' : 'bg-rose-100 text-rose-600'}`}>
                        {index + 1}
                    </div>
                    <div>
                        <p className="font-bold text-gray-700 text-sm">{summary}</p>
                        {data.start_date && (
                            <p className="text-xs text-gray-500">{formatDateForDisplay(data.start_date)}</p>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button 
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove();
                        }}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                        title="Remove Item"
                    >
                        {/* Trash Icon */}
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                    <button
                        type="button"
                        className={`p-1 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                </div>
            </div>

            {/* Collapsible Body */}
            {isExpanded && (
                <div className="p-5 border-t border-gray-100 bg-white animate-fadeIn">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                         <div className="lg:col-span-1">
                             <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Amount</label>
                             <div className="relative">
                                 <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                                 <BufferedInput 
                                    type="number" 
                                    min="0"
                                    className="input-modern pl-7 w-full font-semibold text-gray-700" 
                                    placeholder="0.00"
                                    value={data.amount || ''}
                                    onCommit={val => {
                                        let sanitized = val === '' ? '' : parseFloat(val);
                                        if (sanitized !== '' && (isNaN(sanitized) || sanitized < 0)) sanitized = 0;
                                        onChange('amount', sanitized);
                                    }}
                                    onWheel={(e) => e.currentTarget.blur()}
                                 />
                             </div>
                         </div>
                         <div>
                             <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Frequency</label>
                             <div className="relative">
                                 <select 
                                     className="input-modern w-full appearance-none bg-white"
                                    value={normalizeAccountOptionValue(data.frequency) || ''}
                                     onChange={e => onChange('frequency', e.target.value)}
                                 >
                                     <option value="">Select...</option>
                                     {FREQUENCY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                 </select>
                                 <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                 </div>
                             </div>
                         </div>
                         <div>
                             <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Start Date</label>
                             <DateInput
                                className="input-modern w-full"
                                value={data.start_date || ''}
                                onChange={val => onChange('start_date', val)}
                                minDate={scheduleBounds.minDate}
                                maxDate={scheduleBounds.maxDate}
                             />
                         </div>
                         <div>
                             <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Method</label>
                             <div className="relative">
                                 <select 
                                     className="input-modern w-full appearance-none bg-white"
                                    value={normalizeAccountOptionValue(data.method) || ''}
                                     onChange={e => onChange('method', e.target.value)}
                                 >
                                     <option value="">Select...</option>
                                     {(isDeposit ? DEPOSIT_METHOD_OPTIONS : WITHDRAWAL_METHOD_OPTIONS).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                 </select>
                                 <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                 </div>
                             </div>
                         </div>
                         
                         {/* Row 2 - Only show for Withdrawals */}
                         {!isDeposit && (
                             <>
                                 <div className="grid grid-cols-2 gap-3 lg:col-span-2">
                                     <div>
                                         <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Fed Tax %</label>
                                         <div className="relative">
                                            <input 
                                                type="number" 
                                                min="0"
                                                className="input-modern pr-7 w-full" 
                                                placeholder={defaults?.federal_tax_withholding || "0"}
                                                value={data.federal_tax_withholding || ''}
                                                onChange={e => {
                                                    let val = e.target.value === '' ? '' : parseFloat(e.target.value);
                                                    if (val !== '' && !isNaN(val)) {
                                                        if (val < 0) val = 0;
                                                        if (val > 100) val = 100;
                                                    }
                                                    onChange('federal_tax_withholding', val);
                                                }}
                                                onWheel={(e) => e.currentTarget.blur()}
                                            />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">%</span>
                                         </div>
                                     </div>
                                     <div>
                                         <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">State Tax %</label>
                                         <div className="relative">
                                            <input 
                                                type="number" 
                                                min="0"
                                                className="input-modern pr-7 w-full" 
                                                placeholder={defaults?.state_tax_withholding || "0"}
                                                value={data.state_tax_withholding || ''}
                                                onChange={e => {
                                                    let val = e.target.value === '' ? '' : parseFloat(e.target.value);
                                                    if (val !== '' && !isNaN(val)) {
                                                        if (val < 0) val = 0;
                                                        if (val > 100) val = 100;
                                                    }
                                                    onChange('state_tax_withholding', val);
                                                }}
                                                onWheel={(e) => e.currentTarget.blur()}
                                            />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">%</span>
                                         </div>
                                     </div>
                                 </div>
                                 <div>
                                     <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Destination Type</label>
                                     <div className="relative">
                                         <select 
                                             className="input-modern w-full appearance-none bg-white"
                                             value={data.destination_type || 'Bank Account'}
                                             onChange={e => onChange('destination_type', e.target.value)}
                                         >
                                             <option value="Bank Account">Bank Account</option>
                                             <option value="Brokerage Account">Brokerage Account</option>
                                         </select>
                                         <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                         </div>
                                     </div>
                                 </div>
                                 <div>
                                     <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Destination Identifier</label>
                                     <div className="relative">
                                         <select
                                             className="input-modern w-full appearance-none bg-white"
                                             value={data.destination_identifier || ''}
                                             onChange={e => onChange('destination_identifier', e.target.value)}
                                         >
                                             <option value="">Select destination</option>
                                             {(() => {
                                                 const destinationType = data.destination_type || 'Bank Account';
                                                 const options = getDestinationIdentifierOptions(household, destinationType, accountId);
                                                 const current = data.destination_identifier;
                                                 const currentInOptions = !current || options.some(opt => opt.value === current);
                                                 return (
                                                     <>
                                                         {!currentInOptions && (
                                                             <option value={current}>
                                                                 {getDestinationIdentifierLabel(household, destinationType, current)}
                                                             </option>
                                                         )}
                                                         {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                     </>
                                                 );
                                             })()}
                                         </select>
                                         <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                         </div>
                                     </div>
                                 </div>
                             </>
                         )}
                        </div>
                </div>
            )}
        </div>
    );
};

const CollapsibleInnerSection = ({ title, isOpen, onToggle, color = "gray", children }) => {
    const colors = {
        emerald: "bg-emerald-50 text-emerald-800 border-emerald-100",
        purple: "bg-purple-50 text-purple-800 border-purple-100",
        blue: "bg-blue-50 text-blue-800 border-blue-100",
        gray: "bg-gray-50 text-gray-800 border-gray-200"
    };
    const theme = colors[color] || colors.gray;

    return (
        <div className={`border ${theme.split(' ')[2]} rounded-xl overflow-hidden mb-6 shadow-sm`}>
            <button
                type="button"
                onClick={onToggle}
                className={`w-full flex items-center justify-between p-4 ${theme} transition-colors hover:opacity-90`}
            >
                <h4 className="font-bold text-sm uppercase tracking-wide flex items-center gap-2">
                    {title}
                </h4>
                <div className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                    <svg className={`w-5 h-5 ${theme.split(' ')[1]}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
            </button>
            {isOpen && (
                <div className="p-6 bg-white animate-fadeIn">
                    {children}
                </div>
            )}
        </div>
    );
};

/** Show fee-based question when both IWS and NFS are enabled, or when neither is (user must choose). Hide when exactly one is enabled (custodian is implied). */
function shouldShowFeeBasedAccountQuestion(custodianTypes) {
    if (!custodianTypes) return true;
    const iws = custodianTypes.iws === true;
    const nfs = custodianTypes.nfs === true;
    return (iws && nfs) || (!iws && !nfs);
}

/** When the fee-based question is hidden, default matches AddAccountModal: NFS-only → false, IWS-only → true. */
function inferredFeeBasedAccountWhenHidden(custodianTypes) {
    if (!custodianTypes) return true;
    return custodianTypes.nfs === true && custodianTypes.iws !== true ? false : true;
}

const Step3AccountSetup = ({ data, updateAccountData, onFinish, onBack, aumAvailability, custodianTypes }) => {
  const formatTimeHorizon = (timeHorizon) => getAccountRiskTimeHorizonLabel(timeHorizon, "");

  const formatLiquidityNeeds = (liquidityReq) => {
      return getLiquidityNeedsLabel(liquidityReq?.amount, '');
  };

  const formatLiquidityTimeHorizon = (liquidityReq) => {
      return getLiquidityTimeHorizonLabel(liquidityReq?.time_horizon, '');
  };

  // Transform API risk_tolerance format to frontend format
  const transformApiToFrontend = (apiRisk) => {
      if (!apiRisk) return null;
      return {
          primary_objective: Array.isArray(apiRisk.invest_objectives) ? apiRisk.invest_objectives[0] : apiRisk.invest_objectives,
          risk_tolerance: apiRisk.risk_tolerance,
          time_horizon: formatTimeHorizon(apiRisk.time_horizon),
          account_decision_making: apiRisk.decision_making,
          source_of_funds: Array.isArray(apiRisk.source_of_funds) ? apiRisk.source_of_funds[0] : apiRisk.source_of_funds,
          primary_use: apiRisk.use_of_funds,
          liquidity_needs: formatLiquidityNeeds(apiRisk.liquidity_requirements),
          liquidity_time_horizon: formatLiquidityTimeHorizon(apiRisk.liquidity_requirements)
      };
  };

  // Check if account has risk_tolerance data (in either API or frontend format)
  const isOverridden = (acc) => {
      const strat = acc.risk_tolerance || {}; 
      // Check for both frontend field names and API field names
      return Object.keys(strat).some(k => 
         ['primary_objective', 'risk_tolerance', 'time_horizon', 'invest_objectives', 'decision_making', 'use_of_funds'].includes(k)
      );
  };

  const hasTransactions = (acc) => {
      return (acc.deposits && acc.deposits.length > 0) || (acc.withdrawals && acc.withdrawals.length > 0);
  };

  const handleAccountChange = (accountId, field, value) => {
      let sanitizedValue = value;
      if (field === 'account_value' && value !== '' && value !== null) {
          const numVal = parseFloat(value);
          if (!isNaN(numVal)) {
              sanitizedValue = Math.max(0, numVal).toString();
          }
      }
      updateAccountData(accountId, { [field]: sanitizedValue });
  };
  
  const handleTransferInfoChange = (accountId, field, value) => {
      const account = data.accounts.find(a => a.id === accountId);
      updateAccountData(accountId, {
          transfer_info: {
              ...(account.transfer_info || {}),
              [field]: value
          }
      });
  };

  const handleStrategyChange = (accountId, field, value) => {
      const account = data.accounts.find(a => a.id === accountId);
      let update = { [field]: value };

      if (field === 'primary_objective') {
          const matchingKey = Object.keys(BFS_MAPPING).find(
              key => BFS_MAPPING[key].invest_objectives?.[0] === value
          );
          if (matchingKey) {
              const defaults = BFS_MAPPING[matchingKey];
              update = {
                  primary_objective: value,
                  risk_tolerance: defaults.risk_tolerance,
                  time_horizon: formatTimeHorizon(defaults.time_horizon),
                  account_decision_making: defaults.decision_making,
                  source_of_funds: Array.isArray(defaults.source_of_funds)
                      ? defaults.source_of_funds[0]
                      : defaults.source_of_funds,
                  primary_use: defaults.use_of_funds,
              };
          }
      }

      updateAccountData(accountId, {
          risk_tolerance: { 
              ...(account.risk_tolerance || {}),
              ...update
          }
      });
  };

  const addTransaction = (accountId, type) => { 
      const account = data.accounts.find(a => a.id === accountId);
      const list = account[type] || [];
      const lastItem = list.length > 0 ? list[list.length - 1] : {};
      
      const newItem = {
          id: Date.now(),
          amount: '',
          frequency: 'Monthly',
          start_date: '',
          method: '',
      };

      // Only add these fields for withdrawals
      if (type === 'withdrawals') {
          newItem.federal_tax_withholding = lastItem.federal_tax_withholding || '';
          newItem.state_tax_withholding = lastItem.state_tax_withholding || '';
          newItem.destination_type = 'Bank Account';
          newItem.destination_identifier = '';
      }

      updateAccountData(accountId, {
          [type]: [...list, newItem]
      });
  };

  const updateTransaction = (accountId, type, index, field, value) => {
      let sanitizedValue = value;
      if (['amount', 'federal_tax_withholding', 'state_tax_withholding'].includes(field) && value !== '' && value !== null) {
          const numVal = parseFloat(value);
          if (!isNaN(numVal)) {
              sanitizedValue = Math.max(0, numVal).toString();
              if (field.includes('tax_withholding')) {
                  sanitizedValue = Math.min(100, parseFloat(sanitizedValue)).toString();
              }
          }
      }
      
      const account = data.accounts.find(a => a.id === accountId);
      const list = [...(account[type] || [])];
      list[index] = { ...list[index], [field]: sanitizedValue };
      updateAccountData(accountId, { [type]: list });
  };

  const removeTransaction = (accountId, type, index) => {
      const account = data.accounts.find(a => a.id === accountId);
      const list = [...(account[type] || [])];
      list.splice(index, 1);
      updateAccountData(accountId, { [type]: list });
  };

  const hasActualApiOverrides = (acc) => {
      const rt = acc.risk_tolerance;
      if (!rt) return false;
      const hasObjective = Array.isArray(rt.invest_objectives)
          ? rt.invest_objectives.length > 0 && !!rt.invest_objectives[0]
          : !!rt.invest_objectives;
      return hasObjective || !!rt.risk_tolerance || !!rt.decision_making || !!rt.use_of_funds;
  };

  const [overrideToggles, setOverrideToggles] = useState(() => {
     const init = {};
     if (data.accounts) {
         data.accounts.forEach(acc => {
             init[acc.id] = hasActualApiOverrides(acc);
         });
     }
     return init;
  });

  // On mount: transform API data for accounts with actual overrides, clear for the rest
  React.useEffect(() => {
      if (data.accounts) {
          data.accounts.forEach(acc => {
              if (hasActualApiOverrides(acc)) {
                  if (acc.risk_tolerance && acc.risk_tolerance.invest_objectives && !acc.risk_tolerance.primary_objective) {
                      const transformed = transformApiToFrontend(acc.risk_tolerance);
                      if (transformed) {
                          updateAccountData(acc.id, { risk_tolerance: transformed });
                      }
                  }
              } else if (acc.risk_tolerance != null) {
                  updateAccountData(acc.id, { risk_tolerance: null });
              }
          });
      }
  }, []); // Run once on mount

  const [transactionToggles, setTransactionToggles] = useState(() => {
     const init = {};
     if (data.accounts) {
         data.accounts.forEach(acc => {
             init[acc.id] = hasTransactions(acc);
         });
     }
     return init;
  });

  const toggleOverride = (accountId, val) => {
      setOverrideToggles(prev => ({ ...prev, [accountId]: val }));
      if (val) {
          const hh = data.investment_strategy_defaults || {};
          const defaultStrategy = {
              primary_objective: Array.isArray(hh.invest_objectives) ? hh.invest_objectives[0] : hh.invest_objectives,
              risk_tolerance: hh.risk_tolerance,
              time_horizon: formatTimeHorizon(hh.time_horizon),
              account_decision_making: hh.decision_making,
              source_of_funds: Array.isArray(hh.source_of_funds) ? hh.source_of_funds[0] : hh.source_of_funds,
              primary_use: hh.use_of_funds,
              liquidity_needs: formatLiquidityNeeds(hh.liquidity_requirements),
              liquidity_time_horizon: formatLiquidityTimeHorizon(hh.liquidity_requirements)
          };
          Object.keys(defaultStrategy).forEach(key => defaultStrategy[key] === undefined && delete defaultStrategy[key]);
          updateAccountData(accountId, { risk_tolerance: defaultStrategy });
      } else {
          updateAccountData(accountId, { risk_tolerance: null });
      }
  };

  const toggleTransactions = (accountId, val) => {
      setTransactionToggles(prev => ({ ...prev, [accountId]: val }));
  };

  // Collapsible Account & Section States
  const [expandedAccounts, setExpandedAccounts] = useState(() => {
     const init = {};
     if (data.accounts) {
         data.accounts.forEach(acc => { init[acc.id] = true; }); // Default Account Cards Open
     }
     return init;
  });

  const [expandedSections, setExpandedSections] = useState({});

  const toggleAccount = (id) => {
      setExpandedAccounts(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSection = (accountId, section) => {
      const key = `${accountId}_${section}`;
      setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Default sections to OPEN if undefined
  const isSectionOpen = (accountId, section) => {
      const key = `${accountId}_${section}`;
      return expandedSections[key] !== false; 
  };

  const accounts = data.accounts || [];
  const resolvedCustodianTypes = custodianTypes ?? data?.custodian_types;
  const showFeeBasedAccountQuestion = shouldShowFeeBasedAccountQuestion(resolvedCustodianTypes);
  const brokerageFlags = computeBrokerageFlags({
    custodian: data?.custodian,
    custodianTypes: resolvedCustodianTypes,
    destinationFirm: data?.destination_firm,
  });

  // Initialize default values for accounts on mount
  React.useEffect(() => {
      if (accounts && accounts.length > 0) {
          const resolved = custodianTypes ?? data?.custodian_types;
          const askFee = shouldShowFeeBasedAccountQuestion(resolved);
          accounts.forEach(account => {
              const updates = {};
              let needsUpdate = false;

              // Fee-based: ask user when both or neither custodian type; else infer from single custodian
              if (account.fee_based_account === null || account.fee_based_account === undefined) {
                  updates.fee_based_account = askFee ? true : inferredFeeBasedAccountWhenHidden(resolved);
                  needsUpdate = true;
              }

              // Initialize transfer_info with defaults
              const transferInfoUpdates = { ...(account.transfer_info || {}) };
              let transferInfoNeedsUpdate = false;

              // Set default is_full_transfer to true if null/undefined
              if (transferInfoUpdates.is_full_transfer === null || transferInfoUpdates.is_full_transfer === undefined) {
                  transferInfoUpdates.is_full_transfer = true;
                  transferInfoNeedsUpdate = true;
              }

              // Set default transfer_type to "Brokerage or Trust Company" if null/undefined
              if (!transferInfoUpdates.transfer_type) {
                  transferInfoUpdates.transfer_type = "Brokerage or Trust Company";
                  transferInfoNeedsUpdate = true;
              }

              if (transferInfoNeedsUpdate) {
                  updates.transfer_info = transferInfoUpdates;
                  needsUpdate = true;
              }

              // Set default proceeds if null/undefined
              if (!account.proceeds) {
                  updates.proceeds = "Hold in Core Account Investment Vehicle";
                  needsUpdate = true;
              }

              // Set default security_purchases if null/undefined
              if (!account.security_purchases) {
                  updates.security_purchases = "Hold in Street Name";
                  needsUpdate = true;
              }

              // Set default distribution_income if null/undefined
              if (!account.distribution_income) {
                  updates.distribution_income = DEFAULT_DIVIDEND_OPTION;
                  needsUpdate = true;
              }

              // Set default margin to false if null
              if (account.margin === null || account.margin === undefined) {
                  updates.margin = false;
                  needsUpdate = true;
              }

              // Set default options to false if null
              if (account.options === null || account.options === undefined) {
                  updates.options = false;
                  needsUpdate = true;
              }

              if (needsUpdate) {
                  updateAccountData(account.id, updates);
              }
          });
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time defaults on step entry; matches prior mount-only behavior
  }, []);

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-10">
       <div className="text-center mb-6">
           <h2 className="text-2xl font-bold text-gray-800">Account Setup</h2>
           <p className="text-gray-600">Configure transfer, contributions, and investment details for each account.</p>
       </div>

       {accounts.map((account, index) => {
           const accountOwnerFirstLabel = getAccountOwnerFirstNamesLabel(account, data?.members || []);
           return (
           <div key={account.id} className="card-wizard bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden mb-8">
               {/* Account Header (Collapsible) */}
               <div 
                   className="bg-gradient-to-r from-gray-50 to-white px-8 py-5 border-b border-gray-200 flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-colors"
                   onClick={() => toggleAccount(account.id)}
               >
                   <div className="flex items-center gap-3">
                       <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-lg">
                           {index + 1}
                       </div>
                       <div>
                            <h3 className="font-bold text-xl text-gray-800">{account.registration || 'Unnamed Registration'}</h3>
                            {accountOwnerFirstLabel && (
                                <p className="text-sm font-semibold text-emerald-800 mt-0.5">
                                    {accountOwnerFirstLabel}
                                </p>
                            )}
                            <p className="text-sm text-gray-500 font-medium">{account.transfer_info?.old_account_number || 'XXXXXX'}</p>
                       </div>
                   </div>
                   <div className="flex items-center gap-3">
                       <span className="badge bg-white border border-gray-300 px-4 py-1.5 rounded-full text-sm font-semibold uppercase tracking-wide text-gray-600 shadow-sm">
                           {account.type || 'Unknown Type'}
                       </span>
                       <div className={`text-gray-400 transition-transform duration-200 ${expandedAccounts[account.id] ? 'rotate-180' : ''}`}>
                           <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                       </div>
                   </div>
               </div>
               
               {expandedAccounts[account.id] && (
                   <div className="p-8 bg-white animate-fadeIn">
                       
                       {/* Section 1: Transfer & Features */}
                       <CollapsibleInnerSection 
                           title="1. Transfer & Features" 
                           color="emerald"
                           isOpen={isSectionOpen(account.id, 'transfer')}
                           onToggle={() => toggleSection(account.id, 'transfer')}
                       >
                           <div className="space-y-8">
                               
                               {/* Subsection A: Account & Transfer Details */}
                               <div>
                                   <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-4 border-b border-gray-100 pb-2">Account & Transfer Details</h5>
                                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                       {/* Fee Based — only when project has both IWS & NFS, or neither (same rule as Add Account) */}
                                       {showFeeBasedAccountQuestion && (
                                       <div className="flex flex-col gap-2">
                                           <label className="label-modern">Is this a fee-based account?</label>
                                           <div className="flex bg-gray-50 rounded-lg p-1 border border-gray-200">
                                               <button 
                                                   onClick={() => handleAccountChange(account.id, 'fee_based_account', true)}
                                                   className={`flex-1 px-4 py-2 rounded-md text-sm font-bold transition-all ${account.fee_based_account !== false ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-500 hover:bg-white/50'}`}
                                               >Yes</button>
                                               <button 
                                                   onClick={() => handleAccountChange(account.id, 'fee_based_account', false)}
                                                   className={`flex-1 px-4 py-2 rounded-md text-sm font-bold transition-all ${account.fee_based_account === false ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-500 hover:bg-white/50'}`}
                                               >No</button>
                                           </div>
                                       </div>
                                       )}

                                       {/* Account Value (Conditional — shown only when AUM tracking is enabled) */}
                                       {account.fee_based_account === false && (!aumAvailability || aumAvailability === 1) && (
                                           <div>
                                               <label className="label-modern">Estimated Account Value</label>
                                               <div className="relative">
                                                   <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                                   <input 
                                                       type="number" 
                                                       min="0"
                                                       className="input-modern pl-7 w-full" 
                                                       placeholder="0.00"
                                                       value={account.account_value || ''}
                                                       onChange={e => handleAccountChange(account.id, 'account_value', e.target.value)}
                                                       onWheel={(e) => e.currentTarget.blur()}
                                                   />
                                               </div>
                                           </div>
                                       )}

                                           {/* Transfer Type (Extent) */}
                                       <div className="flex flex-col gap-2">
                                           <label className="label-modern">Transfer Extent</label>
                                           <div className="flex bg-gray-50 rounded-lg p-1 border border-gray-200">
                                               <button 
                                                   onClick={() => handleTransferInfoChange(account.id, 'is_full_transfer', true)}
                                                   className={`flex-1 px-4 py-2 rounded-md text-sm font-bold transition-all ${!account.transfer_info || account.transfer_info.is_full_transfer !== false ? 'bg-white text-emerald-600 shadow-sm border border-gray-100' : 'text-gray-500 hover:bg-white/50'}`}
                                               >Full</button>
                                               <button 
                                                   onClick={() => handleTransferInfoChange(account.id, 'is_full_transfer', false)}
                                                   className={`flex-1 px-4 py-2 rounded-md text-sm font-bold transition-all ${account.transfer_info?.is_full_transfer === false ? 'bg-white text-emerald-600 shadow-sm border border-gray-100' : 'text-gray-500 hover:bg-white/50'}`}
                                               >Partial</button>
                                           </div>
                                       </div>

                                       {/* Transfer Source Type */}
                                       <div>
                                           <label className="label-modern">Transfer Type</label>
                                           <div className="relative">
                                               <select 
                                                    className="input-modern w-full appearance-none bg-white"
                                                    value={account.transfer_info?.transfer_type || 'Brokerage or Trust Company'}
                                                    onChange={e => handleTransferInfoChange(account.id, 'transfer_type', e.target.value)}
                                               >
                                                   {TYPE_OF_TRANSFER_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                               </select>
                                               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                               </div>
                                           </div>
                                       </div>
                                   </div>
                               </div>

                               {/* Subsection B: Standing Instructions */}
                               <div>
                                   <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-4 border-b border-gray-100 pb-2">Standing Instructions</h5>
                                   <div className="space-y-6">
                                       {/* Proceeds */}
                                       <div>
                                           <label className="label-modern mb-2">How should proceeds be handled?</label>
                                           <div className="relative">
                                               <select 
                                                    className="input-modern w-full appearance-none bg-white font-medium text-gray-700"
                                                    value={normalizeAccountOptionValue(account.proceeds) || PROCEEDS_OPTIONS[0]}
                                                    onChange={e => handleAccountChange(account.id, 'proceeds', e.target.value)}
                                               >
                                                   <option value="">Select...</option>
                                                   {PROCEEDS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                               </select>
                                               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                               </div>
                                           </div>
                                       </div>

                                       {/* Security Purchases */}
                                       <div>
                                           <label className="label-modern mb-2">How should security purchases be handled?</label>
                                           <div className="relative">
                                               <select 
                                                    className="input-modern w-full appearance-none bg-white font-medium text-gray-700"
                                                    value={normalizeAccountOptionValue(account.security_purchases) || SECURITY_PURCHASES_OPTIONS[0]}
                                                    onChange={e => handleAccountChange(account.id, 'security_purchases', e.target.value)}
                                               >
                                                   <option value="">Select...</option>
                                                   {SECURITY_PURCHASES_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                               </select>
                                               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                               </div>
                                           </div>
                                       </div>

                                       {/* Dividend Income */}
                                       <div>
                                           <label className="label-modern mb-2">How should dividend/distribution income be handled?</label>
                                           <div className="relative">
                                               <select 
                                                    className="input-modern w-full appearance-none bg-white font-medium text-gray-700"
                                                    value={normalizeAccountOptionValue(account.distribution_income) || DEFAULT_DIVIDEND_OPTION}
                                                    onChange={e => handleAccountChange(account.id, 'distribution_income', e.target.value)}
                                               >
                                                   <option value="">Select...</option>
                                                   {DIVIDEND_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                               </select>
                                               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                               </div>
                                           </div>
                                       </div>
                                   </div>
                               </div>

                               {/* Subsection C: Features */}
                               <div>
                                   <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-4 border-b border-gray-100 pb-2">Account Features</h5>
                                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                       <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${account.margin ? 'border-emerald-500 bg-emerald-50' : 'border-gray-100 hover:border-emerald-200 bg-white'}`}>
                                           <div className={`w-6 h-6 rounded-md border flex items-center justify-center transition-colors ${account.margin ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-gray-300'}`}>
                                               {account.margin && <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                                           </div>
                                           <input 
                                               type="checkbox" 
                                               className="hidden"
                                               checked={!!account.margin} 
                                               onChange={e => handleAccountChange(account.id, 'margin', e.target.checked)}
                                           />
                                           <div>
                                               <span className={`font-bold block ${account.margin ? 'text-emerald-800' : 'text-gray-700'}`}>Enable Margin</span>
                                               <span className="text-xs text-gray-500">Allows borrowing against securities</span>
                                           </div>
                                       </label>
                                       
                                       <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${account.options ? 'border-emerald-500 bg-emerald-50' : 'border-gray-100 hover:border-emerald-200 bg-white'}`}>
                                           <div className={`w-6 h-6 rounded-md border flex items-center justify-center transition-colors ${account.options ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-gray-300'}`}>
                                               {account.options && <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                                           </div>
                                           <input
                                               type="checkbox"
                                               className="hidden"
                                               checked={!!account.options}
                                               onChange={e => handleAccountChange(account.id, 'options', e.target.checked)}
                                           />
                                           <div>
                                               <span className={`font-bold block ${account.options ? 'text-emerald-800' : 'text-gray-700'}`}>Enable Options</span>
                                               <span className="text-xs text-gray-500">Trading options contracts</span>
                                           </div>
                                       </label>

                                       {brokerageFlags.isWaverly && (
                                           <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${account.is_planned !== false ? 'border-emerald-500 bg-emerald-50' : 'border-gray-100 hover:border-emerald-200 bg-white'}`}>
                                               <div className={`w-6 h-6 rounded-md border flex items-center justify-center transition-colors ${account.is_planned !== false ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-gray-300'}`}>
                                                   {account.is_planned !== false && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                                               </div>
                                               <input
                                                   type="checkbox"
                                                   className="hidden"
                                                   checked={account.is_planned !== false}
                                                   onChange={e => handleAccountChange(account.id, 'is_planned', e.target.checked)}
                                               />
                                               <div>
                                                   <span className={`font-bold block ${account.is_planned !== false ? 'text-emerald-800' : 'text-gray-700'}`}>Planning Account</span>
                                                   <span className="text-xs text-gray-500">Is this a Planning Account?</span>
                                               </div>
                                           </label>
                                       )}
                                   </div>
                               </div>

                           </div>
                       </CollapsibleInnerSection>


                       {/* Section 2: Contributions */}
                       <CollapsibleInnerSection 
                           title="2. Contributions & Withdrawals" 
                           color="purple"
                           isOpen={isSectionOpen(account.id, 'contributions')}
                           onToggle={() => toggleSection(account.id, 'contributions')}
                       >
                           <div className="space-y-6">
                               <div className="flex items-center justify-between mb-4 bg-purple-50 p-3 rounded-lg">
                                   <p className="text-sm text-purple-800 font-medium">Do you want to add any?</p>
                                    <div className="flex bg-white rounded-lg p-1 border border-purple-200">
                                       <button 
                                           onClick={() => toggleTransactions(account.id, true)}
                                           className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${transactionToggles[account.id] ? 'bg-purple-600 text-white shadow' : 'text-gray-500 hover:bg-gray-100'}`}
                                       >Yes</button>
                                       <button 
                                           onClick={() => toggleTransactions(account.id, false)}
                                           className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${!transactionToggles[account.id] ? 'bg-purple-600 text-white shadow' : 'text-gray-500 hover:bg-gray-100'}`}
                                       >No</button>
                                   </div>
                               </div>

                               {transactionToggles[account.id] && (
                                   <div className="space-y-8 animate-fadeIn">
                                       {/* Deposits */}
                                       <div>
                                           <div className="flex items-center justify-between mb-3">
                                               <h5 className="font-bold text-sm text-gray-700 uppercase tracking-wide">Contributions</h5>
                                               <button onClick={() => addTransaction(account.id, 'deposits')} className="text-purple-600 text-sm font-bold hover:underline">+ Add</button>
                                           </div>
                                           <div className="space-y-3">
                                               {(!account.deposits || account.deposits.length === 0) && (
                                                   <div className="text-sm text-gray-400 italic bg-gray-50 p-4 rounded-xl border border-dashed border-gray-200 text-center">No Contributions.</div>
                                               )}
                                               {account.deposits && account.deposits.map((item, idx) => (
                                                   <TransactionItem 
                                                       key={item.id || idx} index={idx} type="Deposits"
                                                       data={item} defaults={account.deposits[0]} 
                                                       onChange={(f, v) => updateTransaction(account.id, 'deposits', idx, f, v)}
                                                       onRemove={() => removeTransaction(account.id, 'deposits', idx)}
                                                   />
                                               ))}
                                           </div>
                                       </div>

                                       {/* Withdrawals */}
                                        <div>
                                           <div className="flex items-center justify-between mb-3">
                                               <h5 className="font-bold text-sm text-gray-700 uppercase tracking-wide">Withdrawals</h5>
                                               <button onClick={() => addTransaction(account.id, 'withdrawals')} className="text-rose-600 text-sm font-bold hover:underline">+ Add</button>
                                           </div>
                                           <div className="space-y-3">
                                               {(!account.withdrawals || account.withdrawals.length === 0) && (
                                                   <div className="text-sm text-gray-400 italic bg-gray-50 p-4 rounded-xl border border-dashed border-gray-200 text-center">No withdrawals.</div>
                                               )}
                                               {account.withdrawals && account.withdrawals.map((item, idx) => (
                                                   <TransactionItem
                                                       key={item.id || idx} index={idx} type="Withdrawals"
                                                       data={item} defaults={account.withdrawals[0]}
                                                       household={data} accountId={account.id}
                                                       onChange={(f, v) => updateTransaction(account.id, 'withdrawals', idx, f, v)}
                                                       onRemove={() => removeTransaction(account.id, 'withdrawals', idx)}
                                                   />
                                               ))}
                                           </div>
                                       </div>
                                   </div>
                               )}
                           </div>
                       </CollapsibleInnerSection>

                       {/* Section 3: Strategy */}
                       <CollapsibleInnerSection 
                           title="3. Investment Strategy" 
                           color="blue"
                           isOpen={isSectionOpen(account.id, 'strategy')}
                           onToggle={() => toggleSection(account.id, 'strategy')}
                       >
                           <div className="space-y-6">
                               <div className="flex items-center justify-between mb-4 bg-blue-50 p-3 rounded-lg">
                                   <p className="text-sm text-blue-800 font-medium">Do you want to change household defaults for this account?</p>
                                    <div className="flex bg-white rounded-lg p-1 border border-blue-200">
                                       <button 
                                           onClick={() => toggleOverride(account.id, true)}
                                           className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${overrideToggles[account.id] ? 'bg-blue-600 text-white shadow' : 'text-gray-500 hover:bg-gray-100'}`}
                                       >Yes</button>
                                       <button 
                                           onClick={() => toggleOverride(account.id, false)}
                                           className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${!overrideToggles[account.id] ? 'bg-blue-600 text-white shadow' : 'text-gray-500 hover:bg-gray-100'}`}
                                       >No</button>
                                   </div>
                               </div>

                               {overrideToggles[account.id] ? (
                                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fadeIn">
                                       {showAccountRiskDetails(brokerageFlags, account) && (
                                       <>
                                       {/* Primary Investment Objective - full width */}
                                       <div className="md:col-span-2">
                                           <label className="label-modern text-blue-800/80">Primary Investment Objective</label>
                                           <div className="relative">
                                               <select 
                                                   className="input-modern w-full appearance-none bg-white border-blue-200 focus:border-blue-400 focus:ring-blue-400"
                                                   value={account.risk_tolerance?.primary_objective || ''}
                                                   onChange={e => handleStrategyChange(account.id, 'primary_objective', e.target.value)}
                                               >
                                                   <option value="">Select...</option>
                                                   {PRIMARY_OBJECTIVE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                               </select>
                                               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-blue-400">
                                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                               </div>
                                           </div>
                                       </div>

                                       {/* Risk Tolerance */}
                                       <div>
                                           <label className="label-modern text-blue-800/80">Risk Tolerance</label>
                                           <div className="relative">
                                               <select 
                                                   className="input-modern w-full appearance-none bg-white border-blue-200 focus:border-blue-400 focus:ring-blue-400"
                                                   value={account.risk_tolerance?.risk_tolerance || ''}
                                                   onChange={e => handleStrategyChange(account.id, 'risk_tolerance', e.target.value)}
                                               >
                                                   <option value="">Select...</option>
                                                   {RISK_TOLERANCE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                               </select>
                                               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-blue-400">
                                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                               </div>
                                           </div>
                                       </div>

                                       {/* Time Horizon */}
                                       <div>
                                           <label className="label-modern text-blue-800/80">Time Horizon</label>
                                           <div className="relative">
                                               <select
                                                   className="input-modern w-full appearance-none bg-white border-blue-200 focus:border-blue-400 focus:ring-blue-400"
                                                   value={account.risk_tolerance?.time_horizon || ''}
                                                   onChange={e => handleStrategyChange(account.id, 'time_horizon', e.target.value)}
                                               >
                                                   <option value="">Select...</option>
                                                   {TIME_HORIZON_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                               </select>
                                               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-blue-400">
                                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                               </div>
                                           </div>
                                       </div>
                                       </>
                                       )}

                                       {/* Account Decision Making - full width */}
                                       {showAccountDecisionMaking(brokerageFlags) && (
                                       <div className="md:col-span-2">
                                           <label className="label-modern text-blue-800/80">Account Decision Making</label>
                                           <div className="relative">
                                               <select
                                                   className="input-modern w-full appearance-none bg-white border-blue-200 focus:border-blue-400 focus:ring-blue-400"
                                                   value={account.risk_tolerance?.account_decision_making || ''}
                                                   onChange={e => handleStrategyChange(account.id, 'account_decision_making', e.target.value)}
                                               >
                                                   <option value="">Select...</option>
                                                   {DECISION_MAKING_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                               </select>
                                               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-blue-400">
                                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                               </div>
                                           </div>
                                       </div>
                                       )}

                                       {/* Source of Funds — Fidelity NFS or NewEdge */}
                                       {showSourceOfFunds(brokerageFlags) && (
                                       <div>
                                           <label className="label-modern text-blue-800/80">Source of Funds</label>
                                           <div className="relative">
                                               <select
                                                   className="input-modern w-full appearance-none bg-white border-blue-200 focus:border-blue-400 focus:ring-blue-400"
                                                   value={account.risk_tolerance?.source_of_funds || ''}
                                                   onChange={e => handleStrategyChange(account.id, 'source_of_funds', e.target.value)}
                                               >
                                                   <option value="">Select...</option>
                                                   {SOURCE_OF_FUNDS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                               </select>
                                               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-blue-400">
                                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                               </div>
                                           </div>
                                       </div>
                                       )}

                                       {/* Use of Funds — NewEdge only */}
                                       {showUseOfFunds(brokerageFlags) && (
                                       <div>
                                           <label className="label-modern text-blue-800/80">Use of Funds</label>
                                           <div className="relative">
                                               <select
                                                   className="input-modern w-full appearance-none bg-white border-blue-200 focus:border-blue-400 focus:ring-blue-400"
                                                   value={account.risk_tolerance?.primary_use || ''}
                                                   onChange={e => handleStrategyChange(account.id, 'primary_use', e.target.value)}
                                               >
                                                   <option value="">Select...</option>
                                                   {PRIMARY_USE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                               </select>
                                               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-blue-400">
                                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                               </div>
                                           </div>
                                       </div>
                                       )}

                                       {/* Liquidity Needs (Amount) — NewEdge only */}
                                       {showLiquidityRequirements(brokerageFlags) && (
                                       <div>
                                           <label className="label-modern text-blue-800/80">Liquidity Needs (Amount)</label>
                                           <div className="relative">
                                               <select
                                                   className="input-modern w-full appearance-none bg-white border-blue-200 focus:border-blue-400 focus:ring-blue-400"
                                                   value={account.risk_tolerance?.liquidity_needs || ''}
                                                   onChange={e => handleStrategyChange(account.id, 'liquidity_needs', e.target.value)}
                                               >
                                                   <option value="">Select...</option>
                                                   {LIQUIDITY_NEEDS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                               </select>
                                               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-blue-400">
                                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                               </div>
                                           </div>
                                       </div>
                                       )}

                                       {/* Liquidity Time Horizon (Years) — NewEdge only */}
                                       {showLiquidityRequirements(brokerageFlags) && (
                                       <div>
                                           <label className="label-modern text-blue-800/80">Liquidity Time Horizon (Years)</label>
                                           <div className="relative">
                                               <select
                                                   className="input-modern w-full appearance-none bg-white border-blue-200 focus:border-blue-400 focus:ring-blue-400"
                                                   value={account.risk_tolerance?.liquidity_time_horizon || ''}
                                                   onChange={e => handleStrategyChange(account.id, 'liquidity_time_horizon', e.target.value)}
                                               >
                                                   <option value="">Select...</option>
                                                   {LIQUIDITY_TIME_HORIZON_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                               </select>
                                               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-blue-400">
                                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                               </div>
                                           </div>
                                       </div>
                                       )}
                                   </div>
                               ) : (
                                   <div className="bg-gray-50 rounded-xl p-8 border border-dashed border-gray-300 text-center text-gray-400 italic">
                                       Using Household Strategy Defaults
                                   </div>
                               )}
                           </div>
                       </CollapsibleInnerSection>
                   </div>
               )}
           </div>
           );
       })}

       {/* Actions */}
       <div className="flex justify-between items-center pt-8 border-t border-gray-200 sticky bottom-0 bg-white/90 backdrop-blur-sm p-4 rounded-xl shadow-lg border-t-0 -mx-4 md:mx-0">
          <button 
            onClick={onBack}
            className="text-gray-500 font-semibold hover:text-gray-700 flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg>
              Back
          </button>
          
          <button 
            onClick={onFinish}
            className="btn-primary px-8 py-3 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold"
          >
              Finish & Save
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
          </button>
      </div>
    </div>
  );
};

export default Step3AccountSetup;
